import * as React from "react";
import { ScrollView, Text, View } from "react-native";
import { RollInLeft } from "react-native-reanimated";
import { useRouter } from "expo-router";
import { zodResolver } from "@hookform/resolvers/zod";
import { MoreHorizontalIcon, PlusIcon, TrashIcon } from "lucide-react-native";
import { observer } from "mobx-react-lite";
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
  useWatch,
} from "react-hook-form";
import { z } from "zod";

import { AccountAvatar } from "~/components/AccountAvatar";
import { AvatarEditor } from "~/components/AvatarEditor";
import { PageMeta } from "~/components/PageMeta";
import { useHonoClient } from "~/hooks/HonoContext";
import { icons } from "~/lib/constants";
import { cn } from "~/lib/utils";
import {
  createAccountSlugValidator,
  displayName,
  slug as slugSchema,
} from "~/lib/validation";
import { useGlobalStore } from "~/stores/global";
import { useAccountStore } from "~/stores/space";
import { Invite, Member } from "~/stores/space/store";
import { Button } from "~/ui/Button";
import { Dropdown } from "~/ui/Dropdown";
import { Form } from "~/ui/Form";
import { SelectInput } from "~/ui/SelectInput";
import { TextInput } from "~/ui/TextInput";

export type Role = "OWNER" | "MEMBER" | "GUEST";

export default observer(function Settings() {
  const { store } = useAccountStore();
  const globalStore = useGlobalStore(true);
  const { type, membersAsArray: members, id } = store;
  const role = globalStore.store.accountById(id)!.role as Role;
  const isPersonal = type == "USER";

  return (
    <ScrollView>
      <PageMeta title={isPersonal ? "Account settings" : "Team settings"} />
      <View className="container gap-12 pt-4">
        <Text className="text-text-primary text-xl font-semibold">
          {isPersonal ? "Account" : "Team"} Settings
        </Text>
        <View className="gap-4">
          <Text className="text-text-primary font-semibold">
            Public profile
          </Text>
          <PublicProfileSettingsForm
            isPersonal={isPersonal}
            role={role}
            onSaved={() =>
              isPersonal ? globalStore.refreshStore() : undefined
            }
          />
        </View>
        {!isPersonal && (
          <>
            <View className="gap-4">
              <Text className="text-text-primary font-semibold">Members</Text>
              <InviteForm
                invites={Array.from(store.invites.values())}
                className="max-w-4xl"
                role={role}
              />
              <MemberList members={members} className="max-w-4xl" role={role} />
            </View>
          </>
        )}
        {/* <View>
        <Text className="text-text-primary font-semibold">Avatar</Text>
      </View>
      <SlugForm accountId={store.id} />
      <View>
        <Text className="text-text-primary font-semibold">Email</Text>
      </View> */}
      </View>
    </ScrollView>
  );
});

const publicProfilFormSchema = z.object({
  displayName,
  bio: z.string().optional(),
  avatar: z.string().optional(),
});

export function PublicProfileSettingsForm({
  isPersonal,
  role,
  onSaved,
}: {
  isPersonal: boolean;
  role: Role;
  onSaved: () => void;
}) {
  const { store } = useAccountStore();
  const { hc } = useHonoClient();

  const form = useForm<z.infer<typeof publicProfilFormSchema>>({
    resolver: zodResolver(publicProfilFormSchema),
    defaultValues: {
      displayName: store.displayName || "",
      bio: store.bio || "",
      avatar: store.avatarUrl || "",
    },
    reValidateMode: "onChange",
  });

  async function onSubmit(data: z.infer<typeof publicProfilFormSchema>) {
    if (role !== "OWNER") {
      console.warn("Only the owner can update the profile");
      return;
    }
    const dirtyFields = form.formState.dirtyFields;
    const dirtyData = (
      Object.keys(dirtyFields) as Array<keyof typeof dirtyFields>
    ).reduce((acc, f) => {
      acc = { ...acc, [f]: data[f] };
      return acc;
    }, {});
    const response = await hc.accounts[":accountId"].$patch({
      param: { accountId: store.id },
      json: dirtyData,
    });
    if (response.ok) {
      onSaved();
    } else {
      if (response.status === 400) {
        // TODO pass validation errors to fields
      }
      console.error("Failed to save profile", await response.text());
    }
  }
  return (
    <FormProvider {...form}>
      <Form onSubmit={form.handleSubmit(onSubmit)} className="max-w-5xl gap-4">
        <View className="flex-col-reverse gap-8 md:flex-row">
          <View className="flex-1 gap-4">
            <Controller
              control={form.control}
              name="displayName"
              render={({ field, fieldState: { error } }) => (
                <View>
                  <TextInput
                    label={isPersonal ? "Display Name" : "Team Name"}
                    errorMessage={error?.message}
                    readOnly={role !== "OWNER"}
                    {...field}
                  />
                </View>
              )}
            />
            <Controller
              control={form.control}
              name="bio"
              render={({ field, fieldState: { error } }) => (
                <View>
                  <TextInput
                    label="Bio"
                    errorMessage={error?.message}
                    multiline
                    readOnly={role !== "OWNER"}
                    {...field}
                  />
                </View>
              )}
            />
          </View>
          <View className="m-auto">
            <Controller
              control={form.control}
              name="avatar"
              render={({ field, fieldState: { error } }) => (
                <AvatarField
                  avatar={field.value}
                  onChange={field.onChange}
                  readOnly={role !== "OWNER"}
                />
              )}
            />
          </View>
        </View>
        <View className="flex-row gap-2">
          <Button
            size="small"
            onPress={form.handleSubmit(onSubmit)}
            isLoading={form.formState.isLoading}
            disabled={role !== "OWNER"}
          >
            Update profile
          </Button>
          {form.formState.isDirty && (
            <Button
              variant="secondary"
              size="small"
              onPress={() => form.reset()}
            >
              Cancel changes
            </Button>
          )}
        </View>
      </Form>
    </FormProvider>
  );
}

function AvatarField(props: React.ComponentProps<typeof AvatarEditor>) {
  const displayName = useWatch({ name: "displayName" });
  return <AvatarEditor fallbackText={displayName} {...props} />;
}

const slugFormSchema = z.object({
  slug: slugSchema,
});
type SlugFormType = z.infer<typeof slugFormSchema>;

function SlugForm({
  isPersonal,
  accountId,
}: {
  isPersonal?: boolean;
  accountId: string;
}) {
  const { store } = useAccountStore();
  const { refreshStore } = useGlobalStore(true);
  const { hc } = useHonoClient();
  const { replace } = useRouter();
  const { slug } = store;
  const schema = React.useMemo(() => {
    const slugValidator = createAccountSlugValidator({
      apiClient: hc,
      accountId,
    });
    return z.object({
      slug: slugSchema.refine(slugValidator, {
        message: "Slug is not available",
      }),
    });
  }, []);
  const form = useForm<SlugFormType>({
    resolver: zodResolver(schema, { async: true }, { mode: "async" }),
    defaultValues: {
      slug: slug || "",
    },
    reValidateMode: "onChange",
  });

  const saveSlug = React.useCallback(async ({ slug }: { slug: string }) => {
    const response = await hc.accounts[":accountId"].$patch({
      param: { accountId: store.id },
      json: { slug },
    });
    if (!response.ok) {
      console.error("Failed to save displaName", response);
    }
    refreshStore();
    replace({
      pathname: "/(app)/(authenticated)/[account]/(account)/settings",
      params: {
        account: slug,
      },
    });
  }, []);
  return (
    <View>
      <FormProvider {...form}>
        <Form onSubmit={form.handleSubmit(saveSlug)}>
          <View>
            <Controller
              control={form.control}
              name="slug"
              render={({ field, fieldState: { error } }) => (
                <View>
                  <TextInput
                    label={isPersonal ? "Account URL" : "Team URL"}
                    hint="This is the ac url "
                    errorMessage={error?.message}
                    {...field}
                  />
                </View>
              )}
            />
            <Button
              onPress={form.handleSubmit(saveSlug)}
              isLoading={form.formState.isSubmitting}
              disabled={form.formState.isValidating}
            >
              Save
            </Button>
          </View>
        </Form>
      </FormProvider>
    </View>
  );
}

const inviteFormSchema = z.object({
  invites: z.array(
    z.object({
      email: z.string().email().toLowerCase(),
      role: z.enum(["OWNER", "MEMBER", "GUEST"]),
    }),
  ),
});

const InviteForm = observer(function InviteForm(props: {
  invites: Invite[];
  role: Role;
  className?: string;
}) {
  const { role, className, invites } = props;
  const { store } = useAccountStore();
  const { hc } = useHonoClient();

  const form = useForm<z.infer<typeof inviteFormSchema>>({
    resolver: zodResolver(inviteFormSchema),
    defaultValues: {
      invites: [{ email: "", role: "MEMBER" }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "invites",
    rules: {
      minLength: 1,
    },
  });

  async function onSubmit(data: z.infer<typeof inviteFormSchema>) {
    console.log(data);
    if (role !== "OWNER") {
      console.warn("Only the owner can send invites");
      return;
    }
    const results = await Promise.all(
      data.invites.map(async (invite, index) => {
        try {
          const response = await hc.accounts[":accountId"].invites.$post({
            param: { accountId: store.id },
            json: invite,
          });
          if (response.ok) {
            return { success: true, index };
          } else {
            const errorData = await response.text();
            return {
              success: false,
              index,
              error: errorData || "Unknown error",
            };
          }
        } catch (error) {
          return { success: false, index, error: "Network error" };
        }
      }),
    );

    let hasErrors = false;
    const indexesToRemove: number[] = [];
    results.forEach((result) => {
      if (!result.success) {
        hasErrors = true;
        form.setError(`invites.${result.index}.email`, {
          type: "manual",
          message: result.error,
        });
      } else {
        indexesToRemove.push(result.index);
      }
    });

    // Remove successful submissions
    indexesToRemove
      .sort((a, b) => b - a)
      .forEach((index) => {
        remove(index);
      });

    // If all submissions were successful, ensure there's at least one empty field
    if (fields.length === 0) {
      append({ email: "", role: "MEMBER" });
    }
  }

  console.log(fields);
  console.log(invites);
  return (
    <FormProvider {...form}>
      <Form onSubmit={form.handleSubmit(onSubmit)}>
        <View
          className={cn(
            "border-divider-strong divide-divider-strong divide-y rounded-md border",
            className,
          )}
        >
          <View className="bg-surface-secondary p-4">
            <Text className="text-text-secondary font-semibold">
              Invites ({invites.length})
            </Text>
          </View>
          {invites.map((invite) => {
            const { id, role: memberRole, email, createdAt } = invite;
            return (
              <View
                key={id}
                className="w-full flex-row items-center justify-between px-4 py-4"
              >
                <Text className="text-text-primary">{email}</Text>
                <Text className="text-text-primary">
                  {createdAt.toLocaleDateString()}
                </Text>
                <View className="flex-row items-center gap-8">
                  <Text className="text-text-secondary capitalize">
                    {memberRole.toLowerCase()}
                  </Text>
                  <Dropdown
                    asChild={true}
                    trigger={
                      <Button
                        variant="ghost"
                        size="small"
                        iconAfter={<MoreHorizontalIcon size={18} />}
                      />
                    }
                    groups={[
                      {
                        items: [
                          {
                            key: "remove",
                            title: "Cancel invite",
                            disabled: role !== "OWNER",
                            onSelect: () => {
                              hc.accounts[":accountId"].invites[
                                ":inviteId"
                              ].$delete({
                                param: { accountId: store.id, inviteId: id },
                              });
                            },
                          },
                          {
                            key: "role",
                            title: "Change role",
                            disabled: role !== "OWNER",
                            onSelect: () => {
                              console.error("not implemented");
                            },
                          },
                        ],
                      },
                    ]}
                  />
                </View>
              </View>
            );
          })}
          <View className="py-2">
            <View className="w-full flex-row items-center gap-2 px-4 py-2">
              <Text className="text-input-label flex-1">Email</Text>
              <Text className="text-input-label flex-1">Role</Text>
              <View className="w-[52px]" />
            </View>
            {fields.map((field, index) => (
              <View
                key={field.id}
                className="mb-2 w-full flex-row items-start justify-stretch gap-2 px-4"
              >
                <Controller
                  control={form.control}
                  name={`invites.${index}.email`}
                  aria-label={`email ${index + 1}`}
                  render={({ field, fieldState: { error } }) => (
                    <TextInput
                      wrapperClassName="flex-1"
                      {...field}
                      errorMessage={error?.message}
                    />
                  )}
                />
                <Controller
                  control={form.control}
                  name={`invites.${index}.role`}
                  aria-label={`role ${index + 1}`}
                  render={({ field, fieldState: { error } }) => (
                    <SelectInput
                      {...field}
                      wrapperClassName="flex-1"
                      onValueChange={field.onChange}
                      options={[
                        { label: "Owner", value: "OWNER" },
                        { label: "Member", value: "MEMBER" },
                        { label: "Guest", value: "GUEST" },
                      ]}
                      errorMessage={error?.message}
                    />
                  )}
                />
                {fields.length > 1 && (
                  <Button
                    variant="outline"
                    onPress={() => remove(index)}
                    iconBefore={<TrashIcon size={18} />}
                  ></Button>
                )}
              </View>
            ))}
            <View className="mb-2 flex-row gap-2 px-4">
              <Button
                variant="secondary"
                size="small"
                onPress={() => append({ email: "", role: "MEMBER" })}
                iconBefore={<PlusIcon size={18} />}
              >
                Add row
              </Button>
              <Button
                size="small"
                onPress={form.handleSubmit(onSubmit)}
                isLoading={form.formState.isLoading}
              >
                Send invites
              </Button>
            </View>
          </View>
        </View>
      </Form>
    </FormProvider>
  );
});

function MemberList(props: {
  members: Member[];
  role: Role;
  className?: string;
}) {
  const { members, role, className } = props;
  return (
    <View
      className={cn(
        "border-divider-strong divide-divider-strong divide-y rounded-md border",
        className,
      )}
    >
      <View className="bg-surface-secondary p-4">
        <Text className="text-text-secondary font-semibold">
          Members ({members.length})
        </Text>
      </View>
      {members.map((member) => {
        const {
          id,
          role: memberRole,
          userAccount: { displayName, slug, avatarUrl },
        } = member;
        return (
          <View
            key={id}
            className="w-full flex-row items-center justify-between px-4 py-4"
          >
            <View className="flex-row gap-4">
              <AccountAvatar
                avatar={avatarUrl}
                fallbackText={displayName || slug}
              />
              <View className="gap-0.5">
                <Text className="text-text-primary">{displayName}</Text>
                <Text className="text-text-secondary">{slug}</Text>
              </View>
            </View>
            <View className="flex-row items-center gap-8">
              <Text className="text-text-secondary capitalize">
                {memberRole.toLowerCase()}
              </Text>
              <Dropdown
                asChild={true}
                trigger={
                  <Button
                    variant="ghost"
                    size="small"
                    iconAfter={<MoreHorizontalIcon size={18} />}
                  />
                }
                groups={[
                  {
                    items: [
                      {
                        key: "remove",
                        title: "Remove from team",
                        disabled: role !== "OWNER",
                        onSelect: () => {
                          console.error("not implemented");
                        },
                      },
                      {
                        key: "role",
                        title: "Change role",
                        disabled: role !== "OWNER",
                        onSelect: () => {
                          console.error("not implemented");
                        },
                      },
                    ],
                  },
                ]}
              />
            </View>
          </View>
        );
      })}
    </View>
  );
}
